/**
 * segmentRegistry.ts  — DT-17
 *
 * Canonical segment key definitions and resolver helpers for per-segment
 * style overrides in the drawing system.
 *
 * Rule: src/lib MUST NOT import from src/demo. This file lives in src/lib.
 */
// ---------------------------------------------------------------------------
// Segment key registry
// ---------------------------------------------------------------------------
/**
 * Maps each multi-segment drawing type to its ordered segment keys.
 * Single-line tools (trendLine, hLine, etc.) are NOT listed here —
 * they have no sub-segments and use drawing.style directly.
 *
 * ── Counting rule for shapes whose builder walks keys ONE PER EDGE ────────
 * The harmonic-pattern builder (`overlayPrimitiveAdapter.ts`,
 * `buildHarmonicPatternPrimitive`) walks this list positionally: key `i` styles
 * the edge `pixels[i] → pixels[i+1]`. Two consequences fix the counts below:
 *
 *   - a shape WITH a dashed closing line loses its last key to that line
 *     (`seqKeys = segmentKeys.slice(0, len - 1)`), so a CLOSED N-point shape
 *     must declare N keys to emit N edges;
 *   - a shape WITHOUT a closing line declares N-1 keys for its N-1 edges.
 *
 * Under-declaring silently drops edges from the rendered shape — the polygon
 * draws open. Over-declaring is clamped by the loop bound
 * (`index + 1 < pixels.length`), so the surplus key is dead: `DrawingInspector`
 * would offer a per-segment style control for a segment that never exists.
 * Four shapes under-declared and shipped open (fixed 2026-09-30, K1). The
 * edge-level guard lives in the consumer repo, reading the rendered
 * `OverlayCanvasPrimitive.segments`: `src/widget/patterns/__tests__/
 * harmonicPatternSegments.test.ts`.
 */
export const SEGMENT_KEYS_BY_TYPE = {
    abcdPattern: ["AB", "BC", "CD"],
    xabcdPattern: ["XA", "AB", "BC", "CD", "XD"],
    channel: ["primary", "secondary"],
    parallelChannel: ["upper", "middle", "lower"],
    pitchfork: ["median", "leftFork", "rightFork"],
    gannFan: ["1x8", "1x4", "1x3", "1x2", "1x1", "2x1", "3x1", "4x1", "8x1"],
    elliottImpulse: ["wave-0", "wave-1", "wave-2", "wave-3", "wave-4", "wave-5"],
    elliottCorrection: ["wave-A", "wave-B", "wave-C"],
    elliottTriangle: ["wave-A", "wave-B", "wave-C", "wave-D", "wave-E"],
    regressionChannel: ["center", "upper", "lower"],
    elliottDoubleCombo: ["wave-W", "wave-X", "wave-Y"],
    elliottTripleCombo: ["wave-W", "wave-X1", "wave-Y", "wave-X2", "wave-Z"],
    // X·A·B·C·D, closed ⇒ 5 keys: 4 legs (XA, AB, BC, CD) + the dashed XC.
    cypherPattern: ["XA", "AB", "BC", "CD", "XC"],
    // LS·LV·H·RV·RS·NL·NR, open ⇒ 6 legs. FIVE keys, not six — see below.
    headAndShoulders: ["leftShoulder", "neckline", "head", "rightShoulder", "rsToNl"],
    // H1·L1·H2·L2·apex, closed ⇒ 5 keys: 4 legs + the dashed closing line.
    trianglePattern: ["h1", "l1", "h2", "l2", "closing"],
    // S·1·A·2·B·3·E, open ⇒ 6 legs, not 7. An N-point open polyline has N-1
    // edges, so a 7th key would be clamped by the builder loop and could never
    // style anything.
    threeDrivesPattern: ["s1", "1", "a", "2", "b", "3"],
    pitchfan: ["median", "fan1", "fan2"],
    fibChannel: ["upper", "lower"],
    fibSpeedResistanceFan: ["1x4", "1x3", "1x2", "1x1", "2x1", "3x1", "4x1"],
    gannBox: ["diagonal", "horizontal", "vertical"],
    gannSquare: ["diagonal", "horizontal", "vertical"],
    gannSquareFixed: ["diagonal", "horizontal", "vertical"],
    circle: ["center", "edge"],
    triangle: ["p1", "p2", "p3"],
    callout: ["box", "tail"],
    disjointChannel: ["upperLine", "lowerLine"],
    flatTopBottom: ["line"],
    schiffPitchfork: ["median", "leftFork", "rightFork"],
    modifiedSchiff: ["median", "leftFork", "rightFork"],
    insidePitchfork: ["median", "leftFork", "rightFork"],
    gannFixed: ["1x8", "1x4", "1x3", "1x2", "1x1", "2x1", "3x1", "4x1", "8x1"],
    trendAngle: ["line"],
    infoLine: ["line"],
    rotatedRectangle: ["border1", "border2", "border3", "border4"],
    // CE24: Drawing Tools Pack 3
    datePriceRange: ["main", "hLine0", "hLine1", "vLine0", "vLine1"],
    anchoredVwap: ["vwap", "upper1", "lower1", "upper2", "lower2"],
    crossLine: ["horizontal", "vertical"],
};
// ---------------------------------------------------------------------------
// headAndShoulders — why five keys for a six-leg shape
// ---------------------------------------------------------------------------
//
// Points are LS·LV·H·RV·RS·NL·NR, so the polyline has six legs. The obvious
// mechanical fix is six keys, and that is WRONG: `buildHarmonicPatternPrimitive`
// already emits the 5th→6th leg itself, in its own `isHeadAndShoulders` branch
// (the neckline, extended 20% past each end and styled amber/dashed). A sixth
// key would walk `pixels[5] → pixels[6]` as an ordinary named segment and the
// neckline branch would then draw the same leg a second time — two primitives
// over one pair of points, one of them un-hit-testable and un-styleable, so
// toggling the key would leave a stray line behind.
//
// Five keys stop the walk one leg early, at RS→NL (`rsToNl`), which the
// extended neckline does not cover. Rendered legs: five named + the extended
// neckline = six, no leg drawn twice.
//
// The first four keys are load-bearing as stored data: `segmentStyles` and
// `segmentVisible` are persisted per key, so they are kept verbatim even though
// key `i` styles leg `i` and the names read one point off ("neckline" actually
// styles LV→H). Renaming them would silently drop every saved override.
// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------
/**
 * Returns the segment keys for the given type, or an empty array when the
 * type has no sub-segments.
 */
export function getSegmentKeys(type) {
    return SEGMENT_KEYS_BY_TYPE[type] ?? [];
}
/**
 * Returns true when the drawing type supports per-segment styling.
 */
export function hasSubSegments(type) {
    const keys = SEGMENT_KEYS_BY_TYPE[type];
    return keys !== undefined && keys.length > 0;
}
/**
 * Resolves the effective DrawingStyle for a named segment.
 *
 * Merge rule: base style (drawing.style) overridden by any partial
 * in drawing.segmentStyles[key]. Returns drawing.style unchanged when
 * the key has no override entry.
 */
export function resolveSegmentStyle(drawing, key) {
    const override = drawing.segmentStyles?.[key];
    if (!override)
        return drawing.style;
    return { ...drawing.style, ...override };
}
/**
 * Returns false when drawing.segmentVisible[key] is explicitly false.
 * All other cases (undefined, true) are treated as visible.
 */
export function isSegmentVisible(drawing, key) {
    return drawing.segmentVisible?.[key] !== false;
}
